import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { MEMORIALS as MEM_IDS } from "@/constants/testIds";

function Flame({ lit }) {
  return (
    <span className="relative inline-flex items-center justify-center w-6 h-8 mr-2 align-middle">
      <span className={`absolute bottom-0 w-1 h-3 bg-tulsi rounded-b`} />
      <span
        className={`absolute bottom-3 w-3 h-4 rounded-full ${lit ? "bg-marigold shadow-[0_0_18px_6px_rgba(242,169,59,0.55)] animate-flame" : "bg-ink/20"}`}
        style={{
          clipPath: "polygon(50% 0%, 90% 55%, 70% 100%, 30% 100%, 10% 55%)",
        }}
      />
    </span>
  );
}

export default function Memorials() {
  const [items, setItems] = useState([]);
  const [litMap, setLitMap] = useState({});

  useEffect(() => {
    api.get("/memorials").then((r) => setItems(r.data.memorials || [])).catch(() => setItems([]));
  }, []);

  async function lightCandle(id) {
    setLitMap((m) => ({ ...m, [id]: true }));
    try {
      const { data } = await api.post("/memorials/candle", { memorial_id: id });
      setItems((prev) => prev.map((m) => (m.id === id ? { ...m, candles: data.candles } : m)));
    } catch { /* noop */ }
  }

  return (
    <section
      id="memorials"
      data-testid={MEM_IDS.root}
      className="relative py-28 md:py-40 bg-ivory-100/60 border-y border-ivory-300"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-7">
            <span className="text-xs uppercase tracking-[0.3em] text-saffron">Celebrate a life · Memorials</span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink leading-[0.95]">
              A place to
              <span className="italic text-tulsi"> light </span>
              a candle.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5">
            <p className="text-ink-50 text-base md:text-lg font-light leading-relaxed">
              Every family has someone whose absence still shapes the room. Create memorial pages,
              share stories and light a candle — carried across the SocialPulse network so distant
              relatives can grieve, and remember, together.
            </p>
          </div>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-6 md:gap-8">
          {items.map((m, i) => {
            const lit = !!litMap[m.id];
            return (
              <motion.div
                key={m.id}
                data-testid={MEM_IDS.card(m.id)}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 sm:col-span-6 lg:col-span-3 group"
              >
                <div className="relative bg-ivory border border-ivory-300 rounded-2xl overflow-hidden">
                  <div className="relative">
                    <div className="arch overflow-hidden">
                      <img
                        src={m.portrait}
                        alt={m.name}
                        className="w-full h-[260px] object-cover grayscale-[0.35] sepia-[0.25] group-hover:grayscale-0 group-hover:sepia-0 transition-[filter] duration-[900ms]"
                      />
                    </div>
                    {/* candle badge */}
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-ivory/90 border border-ivory-300 text-[11px]">
                      <span className="w-1.5 h-1.5 rounded-full bg-marigold animate-flame" />
                      <span data-testid={MEM_IDS.candleCount(m.id)}>{m.candles.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-saffron">{m.role}</div>
                    <div className="mt-1 font-serif text-2xl text-ink tracking-tight leading-tight">{m.name}</div>
                    <div className="mt-1 text-xs text-ink-50">{m.years} · {m.hometown}</div>
                    <p className="mt-3 text-sm text-ink-50 font-light leading-relaxed italic border-l border-saffron/40 pl-3">
                      “{m.epitaph}”
                    </p>
                    <button
                      onClick={() => lightCandle(m.id)}
                      data-testid={MEM_IDS.candleBtn(m.id)}
                      className={`mt-5 inline-flex items-center rounded-full border px-4 py-2 text-sm transition ${
                        lit
                          ? "border-marigold text-tulsi bg-marigold/20"
                          : "border-ivory-300 text-ink-50 hover:border-ink hover:text-ink"
                      }`}
                    >
                      <Flame lit={lit} />
                      {lit ? "Candle lit" : "Light a candle"}
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <a
            href="#final-cta"
            className="ink-link text-sm text-ink-50 hover:text-ink"
          >
            Create a memorial for someone you love →
          </a>
        </div>
      </div>
    </section>
  );
}
